import React, { useEffect, useState } from 'react';  
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';  
import { Button } from '../../components/ui/button';  
import { Input } from '../../components/ui/input';  
import { Label } from '../../components/ui/label';  
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';  
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';  
import { Badge } from '../../components/ui/badge';  
import { useToast } from '../../hooks/use-toast';  
import { User, Mail, Phone, BookOpen, Calendar, Save, Upload } from 'lucide-react';  
import { getFirestore, query, where, getDocs, collection, updateDoc, doc } from "firebase/firestore";  
import { app } from "../../firebase"; // Ensure your firebase app is correctly initialized  
import { useLoading } from '../../hooks/useLoading';

export const Profile = () => {  
  const [isEditing, setIsEditing] = useState(false);  
  const { toast } = useToast();  
  const [userDataModel, setUserData] = useState<any>({});  
  
  // Profile data state  
  const [profile, setProfile] = useState({  
    studentsName: '',  
    emailId: '',  
    mobileNumber: '',  
    permanentFullAddress: '',  
    bio: '',  
    joinDate: '',  
    studentIdNo: '',  
    enrolledCourses: 0,  
    completedCourses: 0,  
  });  

  // Form state  
  const [formData, setFormData] = useState({ ...profile });  

  // Firebase Firestore database  
  const db = getFirestore(app);  
   const { setIsLoading } = useLoading();
 
 

  useEffect(() => {  
    // Get email from local storage  
    const { emailId } = JSON.parse(localStorage.getItem('user') || 'null') ?? {}; // Update with your actual key  

    if (emailId) {  
      setIsLoading(true);
      const fetchUserData = async () => {  
        try {  
          const userQuery = query(collection(db, "students"), where("emailId", "==", emailId));  
          const querySnapshot = await getDocs(userQuery);  
          
          if (!querySnapshot.empty) {  
            const userData = querySnapshot.docs[0].data();  
            setUserData(userData);  
            setProfile({  
              studentsName: userData.studentsName,  
              emailId: userData.emailId,  
              mobileNumber: userData.mobileNumber,  
              permanentFullAddress: userData.permanentFullAddress,  
              bio: userData.bio,  
              joinDate: new Date(userData.dateOfBirth.seconds * 1000).toLocaleDateString(),  
              studentIdNo: userData.studentIdNo,  
              enrolledCourses: userData.courses.length,  
              completedCourses: userData.completedCourses,  
            });  
            setFormData({  
              studentsName: userData.studentsName,  
              emailId: userData.emailId,  
              mobileNumber: userData.mobileNumber,  
              permanentFullAddress: userData.permanentFullAddress,  
              bio: userData.bio,  
              joinDate: new Date(userData.dateOfBirth.seconds * 1000).toLocaleDateString(),  
              studentIdNo: userData.studentIdNo,  
              enrolledCourses: userData.courses.length,  
              completedCourses: userData.completedCourses,  
            }); 
            setIsLoading(false); 
          } else {  
            toast({  
              title: "User Not Found",  
              description: "No user data found for this email.",  
            });  
          }  
        } catch (error) {  
          console.error("Error fetching user data:", error);  
          toast({  
            title: "Error",  
            description: "Failed to fetch user data.",  
          });  
        }  
      };  

      fetchUserData();  
    }  
  }, [db, toast]);  

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {  
    const { name, value } = e.target;  
    setFormData({ ...formData, [name]: value });  
  };  

  
  
  const updateUserData = async () => {  
    const { emailId } = formData;  

    // Reference to the document to update  
    const userQuery = query(collection(db, "students"), where("emailId", "==", emailId));   
    const querySnapshot = await getDocs(userQuery);  
    
    if (!querySnapshot.empty) {  
      const userData:any = querySnapshot.docs[0].data();  
      // Update the document with new data  
      await updateDoc(doc(db, "students", userData.id), {  
        ...userData,
        studentsName: formData.studentsName,  
        mobileNumber: formData.mobileNumber,  
        permanentFullAddress: formData.permanentFullAddress ?? '',  
        bio: formData.bio ?? '',  
      });  
      toast({  
        title: "Profile Updated",  
        description: "Your profile information has been updated successfully",  
      });  
      
      // Update local profile state if needed  
      setProfile({ ...formData });  
    } else {  
      toast({  
        title: "Error",  
        description: "Failed to update user data.",  
      });  
    }  
  };  

  const handleEditToggle = () => {  
    if (isEditing) {  
      // Save changes and update data  
      updateUserData();  
    }  
    setIsEditing(!isEditing);  
  };  
  
  const handleCancelEdit = () => {  
    setFormData({ ...profile });  
    setIsEditing(false);  
  };  

  return (  
    <div className="space-y-8 animate-fade-in">  
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">  
        <div className="lg:col-span-1 space-y-6">  
          <Card className="shadow-soft">  
            <CardContent className="p-6">  
              <div className="flex flex-col items-center text-center">  
                <div className="relative">  
                  <Avatar className="w-32 h-32 border-4 border-white shadow-md">  
                    <AvatarImage src="" />  
                    <AvatarFallback className="text-4xl bg-bytesplash-primary text-white">  
                      {profile.studentsName ? profile.studentsName.split(' ').map(n => n[0]).join('') : '?'}  
                    </AvatarFallback>  
                  </Avatar>  
                  <Button   
                    size="icon"   
                    className="absolute bottom-0 right-0 rounded-full bg-bytesplash-primary hover:bg-bytesplash-dark"  
                  >  
                    <Upload size={16} />  
                  </Button>  
                </div>  
                <h2 className="text-xl font-bold mt-4 mb-1">{profile.studentsName}</h2>  
                <Badge className="bg-bytesplash-light text-bytesplash-primary hover:bg-bytesplash-light">  
                  Student  
                </Badge>  
                
                <div className="w-full border-t border-bytesplash-gray-200 my-4" />  
                
                <div className="grid grid-cols-2 gap-4 w-full text-center">  
                  <div>  
                    <p className="text-bytesplash-gray-500 text-sm">Enrolled</p>  
                    <p className="font-bold text-lg">{profile.enrolledCourses}</p>  
                  </div>  
                  <div>  
                    <p className="text-bytesplash-gray-500 text-sm">Completed</p>  
                    <p className="font-bold text-lg">{profile.completedCourses}</p>  
                  </div>  
                </div>  
                
                <div className="w-full border-t border-bytesplash-gray-200 my-4" />  
                
                <div className="w-full flex flex-col space-y-3">  
                  <div className="flex items-center gap-3">  
                    <User size={18} className="text-bytesplash-gray-500" />  
                    <span className="text-bytesplash-gray-700">{profile.studentIdNo}</span>  
                  </div>  
                  <div className="flex items-center gap-3">  
                    <Mail size={18} className="text-bytesplash-gray-500" />  
                    <span className="text-bytesplash-gray-700">{profile.emailId}</span>  
                  </div>  
                  <div className="flex items-center gap-3">  
                    <Phone size={18} className="text-bytesplash-gray-500" />  
                    <span className="text-bytesplash-gray-700">{profile.mobileNumber}</span>  
                  </div>  
                  <div className="flex items-center gap-3">  
                    <Calendar size={18} className="text-bytesplash-gray-500" />  
                    <span className="text-bytesplash-gray-700">Joined {profile.joinDate}</span>  
                  </div>  
                </div>  
              </div>  
            </CardContent>  
          </Card>  

          <Card className="shadow-soft">  
            <CardHeader className="pb-3">  
              <CardTitle className="text-lg">My Achievements</CardTitle>  
            </CardHeader>  
            <CardContent>  
              <div className="flex flex-col items-center py-6">  
                <div className="w-16 h-16 rounded-full bg-bytesplash-light flex items-center justify-center mb-3">  
                  <BookOpen size={24} className="text-bytesplash-primary" />  
                </div>  
                <p className="text-bytesplash-gray-500 text-center">  
                  Complete your first course to earn badges and certificates.  
                </p>  
              </div>  
            </CardContent>  
          </Card>  
        </div>  

        <div className="lg:col-span-2">  
          <Tabs defaultValue="info" className="space-y-6">  
            <TabsList>  
              <TabsTrigger value="info">Personal Information</TabsTrigger>  
              <TabsTrigger value="security">Security</TabsTrigger>  
              <TabsTrigger value="preferences">Preferences</TabsTrigger>  
            </TabsList>  
            <TabsContent value="info">  
              <Card className="shadow-soft">  
                <CardHeader className="pb-4 flex flex-row items-center justify-between">  
                  <div>  
                    <CardTitle>Personal Information</CardTitle>  
                    <CardDescription>  
                      Update your personal details  
                    </CardDescription>  
                  </div>  
                  <Button   
                    onClick={handleEditToggle}  
                    className={`${isEditing ? 'bg-green-600 hover:bg-green-700' : 'bg-bytesplash-primary hover:bg-bytesplash-dark'}`}  
                  >  
                    {isEditing ? (  
                      <><Save size={16} className="mr-2" /> Save</>  
                    ) : (  
                      'Edit Profile'  
                    )}  
                  </Button>  
                </CardHeader>  
                <CardContent>  
                  <form className="space-y-4">  
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">  
                      <div className="space-y-2">  
                        <Label htmlFor="studentsName">Full Name</Label>  
                        <Input  
                          id="studentsName"  
                          name="studentsName"  
                          placeholder="Your name"  
                          value={formData.studentsName}  
                          onChange={handleInputChange}  
                          disabled={!isEditing}  
                          className="form-input"  
                        />  
                      </div>  
                      <div className="space-y-2">  
                        <Label htmlFor="emailId">Email</Label>  
                        <Input  
                          id="emailId"  
                          name="emailId"  
                          type="email"  
                          placeholder="Your email"  
                          value={formData.emailId}  
                          onChange={handleInputChange}  
                          disabled={true}  
                          className="form-input"  
                        />  
                      </div>  
                    </div>  

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">  
                      <div className="space-y-2">  
                        <Label htmlFor="mobileNumber">Phone</Label>  
                        <Input  
                          id="mobileNumber"  
                          name="mobileNumber"  
                          placeholder="Your phone number"  
                          value={formData.mobileNumber}  
                          onChange={handleInputChange}  
                          disabled={!isEditing}  
                          className="form-input"  
                        />  
                      </div>  
                      <div className="space-y-2">  
                        <Label htmlFor="studentIdNo">Student ID</Label>  
                        <Input  
                          id="studentIdNo"  
                          value={formData.studentIdNo}  
                          disabled  
                          className="form-input bg-bytesplash-gray-100"  
                        />  
                      </div>  
                    </div>  

                    <div className="space-y-2">  
                      <Label htmlFor="permanentFullAddress">Address</Label>  
                      <Input  
                        id="permanentFullAddress"  
                        name="permanentFullAddress"  
                        placeholder="Your address"  
                        value={formData.permanentFullAddress}  
                        onChange={handleInputChange}  
                        disabled={!isEditing}  
                        className="form-input"  
                      />  
                    </div>  

                    <div className="space-y-2">  
                      <Label htmlFor="bio">Bio</Label>  
                      <Input  
                        id="bio"  
                        name="bio"  
                        placeholder="A short bio about yourself"  
                        value={formData.bio}  
                        onChange={handleInputChange}  
                        disabled={!isEditing}  
                        className="form-input"  
                      />  
                    </div>  

                    {isEditing && (  
                      <div className="flex justify-end">  
                        <Button   
                          type="button"   
                          variant="outline"   
                          onClick={handleCancelEdit}  
                          className="mr-2"  
                        >  
                          Cancel  
                        </Button>  
                      </div>  
                    )}  
                  </form>  
                </CardContent>  
              </Card>  
            </TabsContent>  

            <TabsContent value="security">  
              <Card className="shadow-soft">  
                <CardHeader>  
                  <CardTitle>Security Settings</CardTitle>  
                  <CardDescription>  
                    Manage your account security preferences  
                  </CardDescription>  
                </CardHeader>  
                <CardContent className="space-y-4">  
                  <div className="space-y-4">  
                    <div className="border rounded-md p-4">  
                      <div className="flex justify-between items-center">  
                        <div>  
                          <h3 className="font-medium">Password</h3>  
                          <p className="text-sm text-bytesplash-gray-500">  
                            Last updated 30 days ago  
                          </p>  
                        </div>  
                        <Button   
                          variant="outline"   
                          className="border-bytesplash-primary text-bytesplash-primary hover:bg-bytesplash-light"  
                          onClick={() => window.location.href = '/profile/change-password'}  
                        >  
                          Change  
                        </Button>  
                      </div>  
                    </div>  

                    <div className="border rounded-md p-4">  
                      <div className="flex justify-between items-center">  
                        <div>  
                          <h3 className="font-medium">Two-Factor Authentication</h3>  
                          <p className="text-sm text-bytesplash-gray-500">  
                            Add an extra layer of security to your account  
                          </p>  
                        </div>  
                        <Button variant="outline">Setup</Button>  
                      </div>  
                    </div>  

                    <div className="border rounded-md p-4">  
                      <div className="flex justify-between items-center">  
                        <div>  
                          <h3 className="font-medium">Active Sessions</h3>  
                          <p className="text-sm text-bytesplash-gray-500">  
                            Manage devices where you're currently logged in  
                          </p>  
                        </div>  
                        <Button variant="outline">View</Button>  
                      </div>  
                    </div>  
                  </div>  
                </CardContent>  
              </Card>  
            </TabsContent>  

            <TabsContent value="preferences">  
              <Card className="shadow-soft">  
                <CardHeader>  
                  <CardTitle>Notification Preferences</CardTitle>  
                  <CardDescription>  
                    Manage how you receive notifications  
                  </CardDescription>  
                </CardHeader>  
                <CardContent>  
                  <p className="text-bytesplash-gray-600 py-6 text-center">  
                    Notification preferences coming soon.  
                  </p>  
                </CardContent>  
              </Card>  
            </TabsContent>  
          </Tabs>  
        </div>  
      </div>  
    </div>  
  );  
};  